import { Dispatch, SetStateAction, useState } from 'react';
import { Drawer, Select } from 'antd';
import * as Yup from 'yup';
import { Formik, Form, FormikHelpers, Field } from 'formik';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { mutateConfig } from '../../../../api/queryBase';
import { getErrorMessage } from '../../../../utils/helper';
import Input from '../../../../components/input/Input';
import Button from '../../../../components/button/Button';
import { TeamMembers } from '../../../../interfaces/User';
import { theme } from '../../../../components/theme/theme';
import { useMediaQuery } from '@mui/material';
import { useBusinessRoles } from '../../../../customhooks/useBusinessRoles';

interface InviteValues {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
}

const InviteUserForm = ({
    viewInviteForm,
    setViewInviteForm,
    refetchUsers,
    setSelected,
    selectedUser,
    isEdit = false,
    setSelectedUser
}: {
    viewInviteForm: boolean;
    setViewInviteForm: Dispatch<SetStateAction<boolean>>;
    refetchUsers: () => void;
    setSelected: (val: string) => void;
    selectedUser?: TeamMembers | null;
    isEdit?: boolean;
    setSelectedUser?: Dispatch<SetStateAction<TeamMembers | null>>;
}) => {
    const closeModal = () => {
        setViewInviteForm(false);
        setSelectedUser && setSelectedUser(null);
    };

    const { isLoading: isRolesLoading, businessRoles } = useBusinessRoles({
        page: 1,
        size: 0
    });

    const initialValues: InviteValues = {
        firstName: selectedUser?.firstName || '',
        lastName: selectedUser?.lastName || '',
        email: selectedUser?.email || '',
        role: selectedUser?.role || ''
    };

    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        email: Yup.string()
            .email('Invalid Email address')
            .required('Email is required'),
        role: Yup.string().required('Role is required')
    });

    const [submitting, setSubmitting] = useState(false);
    const mutation = mutateConfig();

    const { mutate } = mutation({
        url: `/users/invite`,
        cacheKeys: `team-members`
    });

    const { mutate: mutateRole } = mutateConfig({ method: 'patch' })({
        url: `users/${selectedUser?.id}/role`,
        cacheKeys: `team-members`
    });

    const handleUpdateRole = (
        values: InviteValues,
        { resetForm }: FormikHelpers<InviteValues>
    ) => {
        setSubmitting(true);
        mutateRole(
            { ...values, roleId: values.role },
            {
                onSuccess: () => {
                    refetchUsers();
                    resetForm();
                    closeModal();
                },
                onError: (error: AxiosError | undefined | unknown) => {
                    toast.error(
                        getErrorMessage(error) ||
                            'An error has occurred. Please try again.'
                    );
                },
                onSettled: (_, error: AxiosError | undefined | unknown) => {
                    setSubmitting(false);
                    if (error) {
                        toast.error(
                            getErrorMessage(error) ||
                                'An error has occurred. Please try again.'
                        );
                    } else {
                        resetForm();
                        toast.success('Enter OTP');
                        refetchUsers();
                        closeModal();
                    }
                }
            }
        );
    };

    const handleSubmit = async (
        values: InviteValues,
        { resetForm }: FormikHelpers<InviteValues>
    ) => {
        setSubmitting(true);
        mutate(
            { ...values, roleId: values.role },
            {
                onSuccess: () => {
                    refetchUsers();
                    resetForm();
                    closeModal();
                },
                onError: (error: AxiosError | undefined | unknown) => {
                    toast.error(
                        getErrorMessage(error) ||
                            'An error has occurred. Please try again.'
                    );
                },
                onSettled: () => {
                    setSubmitting(false);
                    refetchUsers();
                }
            }
        );
    };

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Drawer
            placement={isMobile ? 'bottom' : 'right'}
            closable={false}
            onClose={() => closeModal()}
            open={viewInviteForm}
            width={430}
            height={'75vh'}
            className='!bg-white !rounded-t-xl pt-6 lg:pt-0 lg:!rounded-t-none'
        >
            <div className='px-4 lg:w-[400px] mx-auto py-4 lg:max-w-[400px] w-full relative'>
                <h2 className='lg:pt-14 pt-0 mb-2 text-[18px] font-semibold text-ebony'>
                    {isEdit ? 'Edit Role' : 'Invite new team member'}
                </h2>
                <h5 className='!text-sm !text-midnightHaze font-light !mb-[24px]'>
                    {isEdit
                        ? 'Make changes to a team member’s profile'
                        : "Enter the email addresses of the users you'd like to invite, and choose the role they should have."}
                </h5>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    onSubmit={isEdit ? handleUpdateRole : handleSubmit}
                >
                    {({ errors, isValid, values, setFieldValue, touched }) => (
                        <Form autoComplete='off' noValidate>
                            <div className='flex flex-col space-y-5'>
                                <Field
                                    as={Input}
                                    type='text'
                                    size='sm'
                                    isError={
                                        touched?.firstName && errors?.firstName
                                    }
                                    label='First name'
                                    error={
                                        touched?.firstName && errors?.firstName
                                    }
                                    disabled={isEdit}
                                    id='firstName'
                                    name='firstName'
                                    placeholder='First Name'
                                />

                                <Field
                                    as={Input}
                                    type='text'
                                    placeholder='Last Name'
                                    isError={
                                        touched?.lastName && errors?.lastName
                                    }
                                    label='Last name'
                                    disabled={isEdit}
                                    error={
                                        touched?.lastName && errors?.lastName
                                    }
                                    id='lastName'
                                    name='lastName'
                                />

                                <Field
                                    as={Input}
                                    label='Email'
                                    type='email'
                                    placeholder='hello@xyz.com'
                                    isError={touched?.email && errors?.email}
                                    disabled={isEdit}
                                    error={touched?.email && errors?.email}
                                    id='email'
                                    name='email'
                                />

                                <div className=''>
                                    <h5 className='block pb-2 text-sm font-medium text-gray700 text-left'>
                                        Select a role
                                    </h5>

                                    <Select
                                        className={`w-full h-[45px] rounded-[8px] text-xs`}
                                        id='role'
                                        placeholder='Role'
                                        disabled={false}
                                        onChange={(value) => {
                                            setFieldValue('role', value);
                                        }}
                                        loading={isRolesLoading}
                                        options={
                                            businessRoles?.map((role) => ({
                                                label: role.name,
                                                value: role.id
                                            })) || []
                                        }
                                    />
                                    {!isEdit && (
                                        <p className='!text-paleSky !text-sm !font-light mt-2'>
                                            To add custom roles, go to{' '}
                                            <span
                                                className='underline cursor-pointer'
                                                onClick={() => {
                                                    setSelected('roles');
                                                }}
                                            >
                                                Manage Roles
                                            </span>
                                            .
                                        </p>
                                    )}
                                </div>

                                <div className=' w-full pt-2 gap-4 flex items-center'>
                                    {!isEdit && (
                                        <Button
                                            className='w-full border border-gray'
                                            onClick={closeModal}
                                            text='Cancel'
                                            variant='tertiaryOutline'
                                            size='sm'
                                        />
                                    )}
                                    <Button
                                        text={isEdit ? 'Update' : 'Save'}
                                        type='submit'
                                        isLoading={submitting}
                                        className='w-full'
                                        size='sm'
                                        disabled={
                                            Object.values(values).some(
                                                (item) => item === ''
                                            ) ||
                                            !isValid ||
                                            submitting
                                        }
                                    />
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Drawer>
    );
};

export default InviteUserForm;
